import { useMemo, useState } from "react";
import { ArrowCounterClockwise, X } from "@phosphor-icons/react";
import { api, formatApiError, money } from "../lib/api";
import { toast } from "sonner";
import PinPromptModal from "./PinPromptModal";

export default function CorrectManualPaymentModal({ saleId, payment, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []);
  const methodLabel = payment?.method === "card" || payment?.method === "card_manual"
    ? "Manual Card"
    : (payment?.method || "Manual payment").replace(/\b\w/g, (letter) => letter.toUpperCase());

  const submit = async (pin) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/sales/${saleId}/payments/correct`, {
        payment_id: payment.payment_id || null,
        payment_index: payment.payment_index,
        reason: reason.trim(),
        idempotency_key: idempotencyKey,
      }, { headers: { "X-Admin-Pin": pin } });
      toast.success(`$${money(payment.amount)} removed from the tender total · balance reopened`);
      onDone?.(data);
      onClose?.();
    } catch (error) {
      toast.error(formatApiError(error));
      throw error;
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[10010] bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="correct-manual-payment-overlay">
        <div className="bg-white w-full max-w-md rounded-2xl border border-slate-200 shadow-2xl overflow-hidden" onClick={(event) => event.stopPropagation()} data-testid="correct-manual-payment-modal">
          <div className="px-6 py-5 border-b border-slate-100 flex items-start justify-between gap-4">
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
                <ArrowCounterClockwise size={19} weight="bold" />
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-700">Manual tender correction</div>
                <h3 className="mt-1 text-[18px] font-bold text-slate-900">Reopen ${money(payment?.amount)}</h3>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors" data-testid="correct-manual-payment-close">
              <X size={15} weight="bold" />
            </button>
          </div>
          <div className="px-6 py-5 space-y-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900" data-testid="correct-manual-payment-notice">
              This corrects the recorded <strong>{methodLabel}</strong> entry only. It does not refund or void a Tyro or Stripe transaction.
            </div>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">Reason</span>
              <textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="e.g. Cash selected by mistake; customer will pay by Tyro" className="w-full min-h-24 px-3 py-2.5 rounded-xl border border-slate-200 text-[13px] text-slate-800 focus:border-amber-500 focus:ring-4 focus:ring-amber-100 focus:outline-none resize-y" data-testid="correct-manual-payment-reason" autoFocus />
            </label>
            <div className="flex gap-2 pt-1">
              <button onClick={onClose} className="flex-1 h-11 rounded-xl border border-slate-200 text-[13px] font-semibold text-slate-700 hover:border-slate-400 transition-colors" data-testid="correct-manual-payment-cancel">Cancel</button>
              <button onClick={() => setPinOpen(true)} disabled={reason.trim().length < 3 || saving} className="flex-1 h-11 rounded-xl bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white text-[13px] font-semibold transition-colors" data-testid="correct-manual-payment-continue">Authorize correction</button>
            </div>
          </div>
        </div>
      </div>
      <PinPromptModal open={pinOpen} title="Authorize payment correction" description="Your admin PIN is required. The original payment stays in the audit history and the invoice balance will reopen." confirmLabel="REOPEN BALANCE" destructive statusEndpoint="/users/me/pin-status" zIndexClass="z-[10020]" onClose={() => setPinOpen(false)} onConfirm={submit} />
    </>
  );
}